import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/authContext';
import { deliveryClient } from '../../../lib/connect/clients';
import { contractsClient } from './Contracts/api';
export async function vehiclePages(read) {
    const items = [];
    let cursor = '';
    const seen = new Set();
    do {
        const page = await read(cursor);
        items.push(...page.items);
        cursor = page.nextAfterId;
        if (cursor && seen.has(cursor)) throw new Error('Repeated pagination cursor. Reload to get the complete visible set.');
        seen.add(cursor);
    }while (cursor)
    return items;
}
export function useVehicleViewer() {
    const { user } = useAuth();
    return {
        tenant: user?.tenant_id ?? '',
        user: user?.id ?? ''
    };
}
export function useVehicleKey(...parts) {
    const viewer = useVehicleViewer();
    return [
        'vehicle-identity',
        viewer.tenant,
        viewer.user,
        ...parts
    ];
}
export function useVehicleBrowse(filters = {}) {
    const viewer = useVehicleViewer();
    return useQuery({
        queryKey: useVehicleKey('browse', filters),
        enabled: !!viewer.tenant && !!viewer.user,
        retry: false,
        queryFn: ()=>vehiclePages(async (afterId)=>{
                const r = await contractsClient.browseVehicles({
                    ...filters,
                    afterId,
                    limit: 100
                });
                return {
                    items: r.vehicles,
                    nextAfterId: r.nextAfterId
                };
            })
    });
}
export function useVehicleProfile(id) {
    const viewer = useVehicleViewer();
    return useQuery({
        queryKey: useVehicleKey('profile', id),
        enabled: !!id && !!viewer.user,
        retry: false,
        queryFn: async ()=>{
            const r = await contractsClient.getVehicleProfile({
                id
            });
            if (!r.profile?.vehicle) throw new Error('Vehicle unavailable');
            return r.profile;
        }
    });
}
export function useVehicleRelationships(vehicleId = '', companyId = '', includeHistory = false) {
    const viewer = useVehicleViewer();
    return useQuery({
        queryKey: useVehicleKey('relationships', vehicleId, companyId, includeHistory),
        enabled: !!viewer.user && !!(vehicleId || companyId),
        retry: false,
        queryFn: ()=>vehiclePages(async (afterId)=>{
                const r = await contractsClient.listVehicleRelationships({
                    vehicleId,
                    companyId,
                    includeHistory,
                    afterId,
                    limit: 100
                });
                return {
                    items: r.relationships,
                    nextAfterId: r.nextAfterId
                };
            })
    });
}
export function useVehicleConnections(vehicleId, kind) {
    const viewer = useVehicleViewer();
    return useQuery({
        queryKey: useVehicleKey('connections', vehicleId, kind),
        enabled: !!viewer.user && !!vehicleId,
        retry: false,
        queryFn: ()=>vehiclePages(async (afterId)=>{
                const r = await contractsClient.listVehicleConnections({
                    vehicleId,
                    kind,
                    afterId,
                    limit: 100
                });
                return {
                    items: r.connections,
                    nextAfterId: r.nextAfterId
                };
            })
    });
}
export function useVehicleCompanies() {
    const viewer = useVehicleViewer();
    return useQuery({
        queryKey: useVehicleKey('companies'),
        enabled: !!viewer.tenant && !!viewer.user,
        retry: false,
        queryFn: async ()=>(await deliveryClient.listCompanyProfiles({
                companyId: viewer.tenant
            })).profiles
    });
}
export function useVehicleHolderContracts(piid) {
    return useQuery({
        queryKey: useVehicleKey('holder-contracts', piid),
        enabled: !!piid,
        retry: false,
        queryFn: ()=>vehiclePages(async (afterId)=>{
                const r = await contractsClient.browseRegistryContracts({
                    piid,
                    afterId,
                    limit: 100
                });
                return {
                    items: r.contracts,
                    nextAfterId: r.nextAfterId
                };
            })
    });
}
export function useVehicleWrites() {
    const cache = useQueryClient();
    const viewer = useVehicleViewer();
    const invalidate = async ()=>{
        await Promise.all([
            cache.invalidateQueries({
                queryKey: [
                    'vehicle-identity',
                    viewer.tenant,
                    viewer.user
                ]
            }),
            cache.invalidateQueries({
                queryKey: [
                    'company evidence',
                    'vehicle holdings'
                ]
            }),
            cache.invalidateQueries({
                queryKey: [
                    'contract vehicle'
                ]
            })
        ]);
    };
    const profile = useMutation({
        mutationFn: (p)=>contractsClient.saveVehicleProfile({
                profile: p,
                expectedRevision: p.vehicle?.revision ?? BigInt(0)
            }),
        onSuccess: invalidate,
        retry: false
    });
    const relationship = useMutation({
        mutationFn: (r)=>contractsClient.saveVehicleRelationship({
                relationship: r,
                expectedRevision: r.revision
            }),
        onSuccess: invalidate,
        retry: false
    });
    const archive = useMutation({
        mutationFn: (r)=>contractsClient.archiveVehicleRelationship({
                id: r.id,
                expectedRevision: r.revision
            }),
        onSuccess: invalidate,
        retry: false
    });
    return {
        profile,
        relationship,
        archive
    };
}
